import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiDownload,
  FiGrid,
  FiList,
  FiColumns,
  FiSearch,
  FiFileText,
  FiPrinter,
  FiMail,
  FiDollarSign,
  FiAlertTriangle,
  FiCalendar,
  FiUser,
  FiCreditCard,
  FiEye,
  FiPackage,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { Modal } from '../../components/UI/Modal';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { facturasService, reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Facturacion = () => {
  const navigate = useNavigate();
  const location = window.location;
  const [facturas, setFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: 'efectivo',
    reference: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Estado para Reporte de Ventas
  const [salesReport, setSalesReport] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesPage, setSalesPage] = useState(1);
  const [salesPerPage, setSalesPerPage] = useState(25);
  const [salesDateFrom, setSalesDateFrom] = useState('');
  const [salesDateTo, setSalesDateTo] = useState('');
  const [salesLoading, setSalesLoading] = useState(false);

  // Aplicar debounce al searchQuery
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Detectar tab desde la URL
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/reporte-ventas')) {
      setActiveTab('reporte-ventas');
    } else if (path.includes('/pagadas')) {
      setActiveTab('pagadas');
    } else if (path.includes('/pendientes')) {
      setActiveTab('pendientes');
    } else if (path.includes('/vencidas')) {
      setActiveTab('vencidas');
    } else {
      setActiveTab('todas');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === 'reporte-ventas') {
      loadSalesReport();
    } else {
      loadFacturas();
    }
  }, [activeTab]);

  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const transformFacturaFromAPI = useCallback((factura) => ({
    id: factura.id,
    numeroFactura: factura.number || factura.invoice_number || `F-${factura.id}`,
    clienteNombre: factura.client_name || factura.cliente?.company_name || factura.cliente?.full_name || 'Cliente no especificado',
    clienteId: factura.client_id || factura.cliente?.id,
    fecha: factura.invoice_date || factura.created_at || '',
    fechaVencimiento: factura.due_date || '',
    total: parseFloat(factura.total) || parseFloat(factura.total_amount) || 0,
    balance: parseFloat(factura.balance) || 0,
    estado: factura.payment_status || factura.status || 'pendiente',
    diasVencida: factura.days_overdue || 0,
    diasVencimiento: factura.days_until_due || 0,
    metodoPago: factura.payment_method || 'terminos',
    vendedor: factura.salesperson || '',
    items: factura.items_count || 0,
    subtotal: parseFloat(factura.subtotal) || 0,
    impuestos: parseFloat(factura.taxes) || 0,
    descuento: parseFloat(factura.discount) || 0,
    notas: factura.notes || '',
    esVencida: factura.is_overdue || false,
    esUrgente: factura.is_urgent || false
  }), []);

  const loadFacturas = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let result;
      const baseParams = {
        skip: 0,
        limit: 100
      };

      // Usar endpoints específicos según la pestaña activa
      switch (activeTab) {
        case 'pagadas':
          // Facturas completamente pagadas
          result = await facturasService.getPaidInvoices(baseParams);
          break;
        case 'pendientes':
          // Facturas sin pagar o parcialmente pagadas
          result = await facturasService.getUnpaidInvoices(baseParams);
          break;
        case 'vencidas':
          // Facturas vencidas
          result = await facturasService.getOverdueInvoices(baseParams);
          break;
        default:
          // Todas las facturas
          result = await facturasService.getFacturas({
            ...baseParams,
            status: activeTab === 'todas' ? undefined : activeTab
          });
      }

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.invoices || []);

        const facturasTransformadas = dataArray.map(transformFacturaFromAPI);
        setFacturas(facturasTransformadas);
      } else {
        // Si no hay servicio implementado, usar datos de ejemplo
        console.warn('API no disponible, usando datos de ejemplo:', result.message);
        loadFacturasEjemplo();
      }
    } catch (error) {
      console.warn('Servicio de facturas no disponible, usando datos de ejemplo:', error);
      loadFacturasEjemplo();
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, transformFacturaFromAPI]);

  const loadFacturasEjemplo = () => {
    const facturasEjemplo = [
      {
        id: 1,
        numero: 'INV-2025-001180',
        fecha: '2025-08-15',
        fechaVencimiento: '2025-09-14',
        cliente: 'Rosa M Ruiz Villarreal',
        nombreNegocio: 'Jalpa Productos Naturales',
        total: 1456.78,
        balance: 1456.78,
        estado: 'vencida',
        diasVencida: 15,
        metodoPago: 'terminos',
        vendedor: 'Juan Pérez',
        items: 8,
        subtotal: 1280.69,
        impuestos: 115.26,
        descuento: 0,
        notas: 'Cliente prefiere entrega en la mañana',
        esVencida: true,
        esUrgente: true
      },
      {
        id: 2,
        numero: 'INV-2025-001205',
        fecha: '2025-08-25',
        fechaVencimiento: '2025-09-24',
        cliente: 'Dr. Miguel Herrera',
        nombreNegocio: 'Farmacia Central',
        total: 892.50,
        balance: 892.50,
        estado: 'pendiente',
        diasVencimiento: 5,
        metodoPago: 'terminos',
        vendedor: 'María González',
        items: 5,
        subtotal: 785.40,
        impuestos: 70.69,
        descuento: 39.27,
        notas: 'Factura mensual',
        esVencida: false,
        esUrgente: false
      },
      {
        id: 3,
        numero: 'INV-2025-001234',
        fecha: '2025-08-28',
        fechaVencimiento: '2025-09-27',
        cliente: 'Carmen López',
        nombreNegocio: 'Naturista El Jardín',
        total: 2340.00,
        balance: 0,
        estado: 'pagada',
        metodoPago: 'cheque',
        vendedor: 'Juan Pérez',
        items: 12,
        subtotal: 2064.60,
        impuestos: 185.81,
        descuento: 103.23,
        notas: 'Pago recibido el 2025-08-30',
        esVencida: false,
        esUrgente: false
      },
      {
        id: 4,
        numero: 'INV-2025-001156',
        fecha: '2025-07-20',
        fechaVencimiento: '2025-08-19',
        cliente: 'Roberto Jiménez',
        nombreNegocio: 'Distribuidora Norte SA',
        total: 3250.75,
        balance: 1625.37,
        estado: 'parcial',
        diasVencida: 10,
        metodoPago: 'terminos',
        vendedor: 'Carlos Rodríguez',
        items: 15,
        subtotal: 2891.74,
        impuestos: 260.26,
        descuento: 144.59,
        notas: 'Pago parcial recibido',
        esVencida: true,
        esUrgente: false
      },
      {
        id: 5,
        numero: 'INV-2025-001267',
        fecha: '2025-08-30',
        fechaVencimiento: '2025-09-29',
        cliente: 'Patricia Morales',
        nombreNegocio: 'Tienda Vida Sana',
        total: 675.20,
        balance: 675.20,
        estado: 'pendiente',
        diasVencimiento: 12,
        metodoPago: 'terminos',
        vendedor: 'Ana Martínez',
        items: 6,
        subtotal: 595.38,
        impuestos: 53.58,
        descuento: 0,
        notas: 'Primera factura del cliente',
        esVencida: false,
        esUrgente: false
      }
    ];

    // Filtrar según la pestaña activa
    let facturasFiltradas = facturasEjemplo;

    if (activeTab === 'pendientes') {
      facturasFiltradas = facturasEjemplo.filter(f => f.estado === 'pendiente' || f.estado === 'parcial');
    } else if (activeTab === 'vencidas') {
      facturasFiltradas = facturasEjemplo.filter(f => f.esVencida);
    } else if (activeTab === 'pagadas') {
      facturasFiltradas = facturasEjemplo.filter(f => f.estado === 'pagada');
    }

    setFacturas(facturasFiltradas);
  };

  const loadSalesReport = useCallback(async () => {
    setSalesLoading(true);
    try {
      const params = {};
      if (salesDateFrom) params.date_from = salesDateFrom;
      if (salesDateTo) params.date_to = salesDateTo;

      const result = await reportesService.getReporteVentasFull(params);

      if (result.success) {
        setSalesReport(result.data?.data || []);
        setSalesSummary(result.data?.summary || null);
        setSalesPage(1);
      } else {
        console.warn('Error cargando reporte de ventas:', result.message);
        setSalesReport([]);
        setSalesSummary(null);
      }
    } catch (error) {
      console.error('Error cargando reporte de ventas:', error);
      setSalesReport([]);
      setSalesSummary(null);
    } finally {
      setSalesLoading(false);
    }
  }, [salesDateFrom, salesDateTo]);

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      loadFacturas();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = {
        skip: 0,
        limit: 100,
        status: activeTab === 'todas' ? undefined : activeTab
      };

      const result = await facturasService.searchFacturas(query, searchParams);

      if (result.success) {
        const facturasData = result.data.map(transformFacturaFromAPI);
        setFacturas(facturasData);
      } else {
        console.warn('Búsqueda no disponible, usando datos de ejemplo:', result.message);
        // Filtrar datos de ejemplo para búsqueda
        loadFacturasEjemplo();
        const facturasFiltered = facturas.filter(f =>
          f.numero.toLowerCase().includes(query.toLowerCase()) ||
          f.nombreNegocio.toLowerCase().includes(query.toLowerCase()) ||
          f.cliente.toLowerCase().includes(query.toLowerCase())
        );
        setFacturas(facturasFiltered);
      }
    } catch (err) {
      console.warn('Error en búsqueda, usando datos de ejemplo:', err);
      // Filtrar datos de ejemplo para búsqueda
      loadFacturasEjemplo();
      const facturasFiltered = facturas.filter(f =>
        f.numero.toLowerCase().includes(query.toLowerCase()) ||
        f.nombreNegocio.toLowerCase().includes(query.toLowerCase()) ||
        f.cliente.toLowerCase().includes(query.toLowerCase())
      );
      setFacturas(facturasFiltered);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, loadFacturas, transformFacturaFromAPI, facturas]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearError = () => {
    setError(null);
    loadFacturas();
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate
      };

      const result = await reportesService.exportarFacturas(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `facturas_${startDate}_${endDate}.${extensions[format]}`
          : `facturas_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Cerrar modal y mostrar mensaje de éxito
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrintFactura = async (factura) => {
    try {
      const result = await facturasService.generateInvoicePDF(factura.id);
      if (result.success) {
        // Crear URL del blob y abrirlo en nueva ventana
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        alert(result.message || 'Error al generar el PDF');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Por favor intente nuevamente.');
    }
  };

  const handlePrintPackingList = async (factura) => {
    try {
      const options = {
        include_prices: false,
        include_totals: false
      };
      const result = await facturasService.generatePackingList(factura.id, options);
      if (result.success) {
        // Crear URL del blob y abrirlo en nueva ventana
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        alert(result.message || 'Error al generar la lista de empaque');
      }
    } catch (error) {
      console.error('Error generando lista de empaque:', error);
      alert('Error al generar la lista de empaque. Por favor intente nuevamente.');
    }
  };

  const handlePayFactura = (e, factura) => {
    e.stopPropagation();
    setSelectedFactura(factura);
    setPaymentData(prev => ({
      ...prev,
      amount: factura.balance || 0
    }));
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedFactura) return;

    try {
      const paymentInfo = {
        payment_amount: parseFloat(paymentData.amount),
        payment_method: paymentData.method,
        payment_date: paymentData.date,
        payment_reference: paymentData.reference,
        notes: paymentData.notes
      };

      const result = await facturasService.markFacturaAsPaid(selectedFactura.id, paymentInfo);

      if (result.success) {
        setShowPaymentModal(false);
        setPaymentData({
          amount: 0,
          method: 'efectivo',
          reference: '',
          notes: '',
          date: new Date().toISOString().split('T')[0]
        });
        setSelectedFactura(null);
        // Recargar facturas
        loadFacturas();
        alert('Pago registrado exitosamente');
      } else {
        alert(result.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error procesando pago:', error);
      alert('Error al procesar el pago. Por favor intente nuevamente.');
    }
  };

  const handleSendReminder = async (factura) => {
    console.log('Enviar recordatorio:', factura.numero);
    try {
      const reminderData = {
        invoice_ids: [factura.id],
        reminder_type: 'overdue',
        custom_message: `Su factura ${factura.numero} está vencida. Por favor proceda con el pago.`
      };
      
      const result = await facturasService.sendPaymentReminder(factura.id, reminderData);
      
      if (result.success) {
        console.log('Recordatorio enviado exitosamente');
        // Aquí puedes agregar una notificación de éxito
      } else {
        console.error('Error enviando recordatorio:', result.message);
      }
    } catch (error) {
      console.error('Error enviando recordatorio:', error);
    }
  };

  const getStatusColor = (estado) => {
    const colors = {
      pendiente: 'warning',
      vencida: 'danger',
      pagada: 'success',
      parcial: 'info',
      cancelada: 'secondary'
    };
    return colors[estado] || 'default';
  };

  const getTotalFacturas = (tipo) => {
    switch (tipo) {
      case 'pendientes':
        return facturas.filter(f => f.estado === 'pendiente' || f.estado === 'parcial').length;
      case 'vencidas':
        return facturas.filter(f => f.esVencida).length;
      case 'pagadas':
        return facturas.filter(f => f.estado === 'pagada').length;
      default:
        return facturas.length;
    }
  };

  const getTotalMonto = (tipo) => {
    let facturasFiltradas = facturas;
    switch (tipo) {
      case 'pendientes':
        facturasFiltradas = facturas.filter(f => f.estado === 'pendiente' || f.estado === 'parcial');
        return facturasFiltradas.reduce((sum, f) => sum + f.balance, 0);
      case 'vencidas':
        facturasFiltradas = facturas.filter(f => f.esVencida);
        return facturasFiltradas.reduce((sum, f) => sum + f.balance, 0);
      case 'pagadas':
        facturasFiltradas = facturas.filter(f => f.estado === 'pagada');
        return facturasFiltradas.reduce((sum, f) => sum + f.total, 0);
      default:
        return facturas.reduce((sum, f) => sum + f.total, 0);
    }
  };

  if (isLoading && activeTab !== 'reporte-ventas') {
    return <LoadingGrid message="Cargando facturas..." />;
  }

  return (
    <div className="facturacion-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">FACTURACIÓN</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          {/* Botón de Nueva Factura deshabilitado - Las facturas se crean desde Tomar Ordenes */}
          {/* <button className="menu-item menu-item--first" onClick={() => navigate('/system/facturacion/nueva')}>
            + NUEVA FACTURA
          </button> */}

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NÚMERO DE FACTURA O CLIENTE"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR REPORTE ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>



      {/* Contenido de Reporte de Ventas */}
      {activeTab === 'reporte-ventas' ? (
        <div className="sales-report-container" style={{ marginBottom: '100px' }}>
          {/* Filtros de fecha */}
          <div className="sales-report-filters" style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Desde</label>
              <input
                type="date"
                className="form-input"
                value={salesDateFrom}
                onChange={(e) => setSalesDateFrom(e.target.value)}
                style={{ padding: '6px 10px', fontSize: '13px' }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Hasta</label>
              <input
                type="date"
                className="form-input"
                value={salesDateTo}
                onChange={(e) => setSalesDateTo(e.target.value)}
                style={{ padding: '6px 10px', fontSize: '13px' }}
              />
            </div>
            <button
              className="btn btn--primary btn--sm"
              onClick={loadSalesReport}
              disabled={salesLoading}
              style={{ height: '34px' }}
            >
              <FiSearch size={14} />
              <span>{salesLoading ? 'Buscando...' : 'Buscar'}</span>
            </button>
          </div>

          {/* Resumen */}
          {salesSummary && (
            <div className="sales-report-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div style={{ background: 'var(--bg-secondary, #f5f5f5)', padding: '14px 16px', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', marginBottom: '4px' }}>Total Registros</div>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>{salesSummary.total_records || 0}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary, #666)' }}>
                  {salesSummary.total_invoices || 0} facturas / {salesSummary.total_orders || 0} órdenes
                </div>
              </div>
              <div style={{ background: 'var(--bg-secondary, #f5f5f5)', padding: '14px 16px', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', marginBottom: '4px' }}>Total Ventas</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--color-primary, #2563eb)' }}>{formatCurrency(salesSummary.total_revenue)}</div>
              </div>
              <div style={{ background: 'var(--bg-secondary, #f5f5f5)', padding: '14px 16px', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', marginBottom: '4px' }}>Total Pagado</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--color-success, #16a34a)' }}>{formatCurrency(salesSummary.total_paid)}</div>
              </div>
              <div style={{ background: 'var(--bg-secondary, #f5f5f5)', padding: '14px 16px', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', marginBottom: '4px' }}>Total Pendiente</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--color-warning, #d97706)' }}>{formatCurrency(salesSummary.total_pending)}</div>
              </div>
            </div>
          )}

          {/* Tabla de datos */}
          {salesLoading ? (
            <LoadingGrid message="Cargando reporte de ventas..." />
          ) : salesReport.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FiFileText size={48} /></div>
              <h3 className="empty-state-title">No hay datos para mostrar</h3>
              <p className="empty-state-description">Ajusta los filtros de fecha y presiona Buscar</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="data-table data-table--facturas">
                  <thead>
                    <tr>
                      <th>FECHA</th>
                      <th>TIPO</th>
                      <th>NÚMERO</th>
                      <th>CLIENTE</th>
                      <th className="text-right">SUBTOTAL</th>
                      <th className="text-right">IMPUESTO</th>
                      <th className="text-right">DESCUENTO</th>
                      <th className="text-right">TOTAL</th>
                      <th className="text-right">PAGADO</th>
                      <th className="text-right">BALANCE</th>
                      <th className="text-center">ESTADO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesReport
                      .slice((salesPage - 1) * salesPerPage, salesPage * salesPerPage)
                      .map((row, idx) => (
                        <tr key={`${row.record_type}-${row.invoice_id}-${idx}`}>
                          <td>{formatDate(row.invoice_date)}</td>
                          <td>
                            <span className={`status-badge ${row.record_type === 'invoice' ? 'status-badge--info' : 'status-badge--secondary'}`}>
                              {row.record_type === 'invoice' ? 'Factura' : 'Orden'}
                            </span>
                          </td>
                          <td>{row.invoice_number}</td>
                          <td><strong>{row.client_name}</strong></td>
                          <td className="text-right">{formatCurrency(row.subtotal)}</td>
                          <td className="text-right">{formatCurrency(row.tax_amount)}</td>
                          <td className="text-right">{formatCurrency(row.discount_amount)}</td>
                          <td className="text-right"><strong>{formatCurrency(row.total)}</strong></td>
                          <td className="text-right">{formatCurrency(row.paid_amount)}</td>
                          <td className="text-right">{formatCurrency(row.balance)}</td>
                          <td className="text-center">{getEstadoBadge(row.status)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Paginador */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary, #666)' }}>
                  Mostrando {Math.min((salesPage - 1) * salesPerPage + 1, salesReport.length)}-{Math.min(salesPage * salesPerPage, salesReport.length)} de {salesReport.length} registros
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="form-input"
                    value={salesPerPage}
                    onChange={(e) => { setSalesPerPage(Number(e.target.value)); setSalesPage(1); }}
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '13px' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <button
                    className="btn btn--outline btn--sm"
                    onClick={() => setSalesPage(p => Math.max(1, p - 1))}
                    disabled={salesPage === 1}
                  >
                    Anterior
                  </button>
                  <span style={{ fontSize: '13px' }}>
                    Pág. {salesPage} / {Math.ceil(salesReport.length / salesPerPage)}
                  </span>
                  <button
                    className="btn btn--outline btn--sm"
                    onClick={() => setSalesPage(p => Math.min(Math.ceil(salesReport.length / salesPerPage), p + 1))}
                    disabled={salesPage >= Math.ceil(salesReport.length / salesPerPage)}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
      /* Contenido de facturas */
      facturas.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron facturas' : 'No hay facturas registradas'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron facturas que coincidan con "${searchQuery}"`
              : 'Comienza creando tu primera factura'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--facturas">
            <thead>
              <tr>
                <th>FECHA</th>
                <th>NEGOCIO</th>
                <th>NÚMERO DE FACTURA</th>
                <th>FECHA DE VENCIMIENTO</th>
                <th className="text-right">TOTAL</th>
                <th className="text-right">BALANCE</th>
                <th className="text-center">PAGAR FACTURA</th>
              </tr>
            </thead>
            <tbody>
              {facturas.map((factura) => (
                <tr
                  key={factura.id}
                  onClick={() => navigate(`/system/facturacion/${factura.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{formatDate(factura.fecha)}</td>
                  <td><strong>{factura.clienteNombre}</strong></td>
                  <td>{factura.numeroFactura}</td>
                  <td>{formatDate(factura.fechaVencimiento)}</td>
                  <td className="text-right">{formatCurrency(factura.total)}</td>
                  <td className="text-right">{formatCurrency(factura.balance)}</td>
                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                    {factura.balance > 0 && (
                      <button
                        className="btn-pay-invoice"
                        onClick={(e) => handlePayFactura(e, factura)}
                        title="Pagar factura"
                      >
                        <FiDollarSign />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {facturas.map((factura) => (
            <div
              key={factura.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/facturacion/${factura.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(factura.estado)}`}>
                    <div className="factura-number">
                      {factura.numeroFactura}
                    </div>
                    <div className="factura-amount">
                      {formatCurrency(factura.total)}
                    </div>
                  </div>
                  <div className="content-info">
                    <div className="content-title">{factura.clienteNombre}</div>
                    <div className="content-subtitle">{formatDate(factura.fecha)}</div>
                    <div className="content-meta">
                      {getEstadoBadge(factura.estado)}
                    </div>
                  </div>
                  <div className="content-card__actions">
                    <FiDollarSign className="card-icon" />
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(factura.estado)}`}>
                    <FiFileText className="thumbnail-icon" />
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{factura.numeroFactura}</div>
                    <div className="content-subtitle">{factura.clienteNombre}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiCalendar size={12} /> {formatDate(factura.fecha)}
                      </span>
                      <span className="meta-item">
                        <FiDollarSign size={12} /> {formatCurrency(factura.total)}
                      </span>
                      {getEstadoBadge(factura.estado)}
                    </div>
                  </div>
                  <div className="content-actions-list">
                    <FiDollarSign className="card-icon" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ))}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Facturas"
        moduleName="facturas"
        isLoading={isDownloading}
      />

      {/* Modal de Pago */}
      {selectedFactura && (
        <Modal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedFactura(null);
          }}
          title={`Registrar Pago - ${selectedFactura.numeroFactura}`}
          size="medium"
        >
          <div className="modal-body">
            <div className="payment-info-summary">
              <p><strong>Cliente:</strong> {selectedFactura.clienteNombre}</p>
              <p><strong>Total de la factura:</strong> {formatCurrency(selectedFactura.total)}</p>
              <p><strong>Saldo pendiente:</strong> {formatCurrency(selectedFactura.balance)}</p>
            </div>

            <div className="form-group">
              <label className="form-label">Monto del Pago *</label>
              <input
                type="number"
                className="form-input"
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                min="0"
                max={selectedFactura.balance}
                step="0.01"
              />
              <small className="form-helper">
                Saldo pendiente: {formatCurrency(selectedFactura.balance)}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Método de Pago *</label>
              <select
                className="form-input"
                value={paymentData.method}
                onChange={(e) => setPaymentData(prev => ({ ...prev, method: e.target.value }))}
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fecha del Pago</label>
              <input
                type="date"
                className="form-input"
                value={paymentData.date}
                onChange={(e) => setPaymentData(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Referencia</label>
              <input
                type="text"
                className="form-input"
                value={paymentData.reference}
                onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Número de cheque, transferencia, etc."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea
                className="form-input"
                value={paymentData.notes}
                onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas adicionales sobre el pago"
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn--outline"
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedFactura(null);
                setPaymentData({
                  amount: 0,
                  method: 'efectivo',
                  reference: '',
                  notes: '',
                  date: new Date().toISOString().split('T')[0]
                });
              }}
            >
              <FiX size={18} />
              <span>Cancelar</span>
            </button>
            <button
              className="btn btn--primary"
              onClick={processPayment}
              disabled={!paymentData.amount || paymentData.amount <= 0 || isLoading}
            >
              <FiCheck size={18} />
              <span>{isLoading ? 'Procesando...' : 'Registrar Pago'}</span>
            </button>
          </div>
        </Modal>
      )}

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute="/system/facturacion"
        currentTab={activeTab}
        tabs={[
          {
            id: 'todas',
            label: 'Todas las Facturas',
            route: ''
          },
          {
            id: 'pagadas',
            label: 'Facturas Pagas',
            route: '/pagadas'
          },
          {
            id: 'pendientes',
            label: 'Facturas Sin Pagar',
            route: '/pendientes'
          },
          {
            id: 'vencidas',
            label: 'Facturas Vencidas',
            route: '/vencidas'
          },
          {
            id: 'reporte-ventas',
            label: 'Reporte de Ventas',
            route: '/system/reportes/ventas'
          }
        ]}
      />
    </div>
  );

  // Funciones helper
  function getThumbnailType(estado) {
    switch (estado?.toLowerCase()) {
      case 'pagada':
      case 'paid':
        return 'green-gradient';
      case 'pendiente':
      case 'pending':
        return 'orange-gradient';
      case 'vencida':
      case 'overdue':
        return 'purple-gradient';
      case 'cancelada':
      case 'cancelled':
        return 'gray';
      default:
        return 'blue-dark-gradient';
    }
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function getEstadoBadge(estado) {
    const badgeClass = {
      'pagada': 'status-badge--success',
      'paid': 'status-badge--success',
      'pendiente': 'status-badge--warning',
      'pending': 'status-badge--warning',
      'vencida': 'status-badge--danger',
      'overdue': 'status-badge--danger',
      'cancelada': 'status-badge--secondary',
      'cancelled': 'status-badge--secondary'
    };

    const estadoTexto = {
      'paid': 'Pagada',
      'pending': 'Pendiente',
      'overdue': 'Vencida',
      'cancelled': 'Cancelada'
    };

    return (
      <span className={`status-badge ${badgeClass[estado?.toLowerCase()] || 'status-badge--info'}`}>
        {estadoTexto[estado?.toLowerCase()] || estado || 'Sin estado'}
      </span>
    );
  }
};

export default Facturacion;